/**
 * Settings → Recording (ROLES-AND-GO): how the capture app runs a recording
 * for this organisation.
 *
 * - Roles: when on, the app asks for a role (Primary nurse, Secondary nurse,
 *   …) on every recording, so a simulation participant can switch roles
 *   between takes without being registered again. The list is edited here.
 * - Start: one Start (as before), or Ready then Go. Ready starts the sensors,
 *   cameras and room audio; Go marks where the procedure begins. Before Go
 *   the recording is in standby, which scores, duration and reports leave
 *   out. Go is a button on the phone (simulation) or a spoken phrase the
 *   phone listens for (the operating room, where nobody touches it).
 */
import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';

import { extractErrorMessage } from '../../api/errors';
import {
  createSessionRole,
  listSessionRoles,
  removeSessionRole,
  reorderSessionRoles,
  updateSessionRole,
  type SessionRole,
} from '../../api/sessionRoles';
import * as tenantsApi from '../../api/tenants';
import type { StartMode, Tenant } from '../../api/tenants';

const START_MODES: { value: StartMode; label: string; help: string }[] = [
  {
    value: 'single',
    label: 'One step',
    help: 'Start begins the procedure, as before.',
  },
  {
    value: 'button',
    label: 'Ready, then a Go button (simulation)',
    help: 'Ready starts the recording; the operator taps Go on the phone when the procedure begins.',
  },
  {
    value: 'voice',
    label: 'Ready, then a spoken phrase (operating room)',
    help: 'Ready starts the recording; the phone listens for the phrase below and starts the procedure when it hears it. Nobody has to touch the phone.',
  },
];

export function RecordingSettings({
  tenant,
  canWrite,
  onTenantChange,
}: {
  tenant: Tenant;
  canWrite: boolean;
  onTenantChange: (t: Tenant) => void;
}) {
  const [roles, setRoles] = useState<SessionRole[] | null>(null);
  const [newRole, setNewRole] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [phrase, setPhrase] = useState(tenant.go_phrase ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setRoles(await listSessionRoles(tenant.id));
    } catch (e) {
      setError(extractErrorMessage(e, 'could not load the roles'));
    }
  }, [tenant.id]);
  useEffect(() => {
    void reload();
  }, [reload]);
  useEffect(() => setPhrase(tenant.go_phrase ?? ''), [tenant.go_phrase]);

  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(extractErrorMessage(e, `could not ${what}`));
    } finally {
      setBusy(false);
    }
  };

  const patchTenant = (patch: Parameters<typeof tenantsApi.updateTenant>[1], what: string) =>
    run(what, async () => onTenantChange(await tenantsApi.updateTenant(tenant.id, patch)));

  const live = (roles ?? []).filter((r) => !r.archived_at);
  const archived = (roles ?? []).filter((r) => r.archived_at);
  const startMode: StartMode = tenant.start_mode ?? 'single';
  const phraseDirty = phrase.trim() !== (tenant.go_phrase ?? '');

  const move = (i: number, delta: number) => {
    const ids = live.map((r) => r.id);
    const j = i + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void run('reorder the roles', async () => {
      await reorderSessionRoles(tenant.id, ids);
      await reload();
    });
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Roles
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            In a simulation a participant may take a different role at each station. With roles
            on, the capture app asks for a role and a procedure for every recording, and{' '}
            <b>Next role</b> goes straight on to the next one with the same participant and
            sensors. The role shows on the session and in reports.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(tenant.session_roles_enabled)}
                disabled={!canWrite || busy}
                onChange={(e) =>
                  void patchTenant(
                    { session_roles_enabled: e.target.checked },
                    'change the roles setting',
                  )
                }
              />
            }
            label="Ask for a role on every recording"
          />
          {tenant.session_roles_enabled && live.length === 0 && roles !== null && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Roles are on but there are none yet — add at least one below.
            </Alert>
          )}

          <Stack spacing={0.5} sx={{ mt: 2 }} aria-label="Roles">
            {live.map((r, i) => (
              <Stack
                key={r.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 0.25 }}
              >
                {editing?.id === r.id ? (
                  <TextField
                    size="small"
                    value={editing.name}
                    autoFocus
                    inputProps={{ maxLength: 80, 'aria-label': `Rename ${r.name}` }}
                    onChange={(e) => setEditing({ id: r.id, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEditing(null);
                      if (e.key === 'Enter' && editing.name.trim()) {
                        void run('rename the role', async () => {
                          await updateSessionRole(tenant.id, r.id, { name: editing.name });
                          setEditing(null);
                          await reload();
                        });
                      }
                    }}
                    onBlur={() => setEditing(null)}
                    sx={{ flex: 1 }}
                  />
                ) : (
                  <Typography
                    sx={{ flex: 1, cursor: canWrite ? 'text' : 'default' }}
                    onClick={() => canWrite && setEditing({ id: r.id, name: r.name })}
                  >
                    {r.name}
                  </Typography>
                )}
                {r.used_count > 0 && (
                  <Chip size="small" variant="outlined" label={`${r.used_count} recordings`} />
                )}
                {canWrite && (
                  <>
                    <IconButton
                      size="small"
                      aria-label={`Move ${r.name} up`}
                      disabled={busy || i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Move ${r.name} down`}
                      disabled={busy || i === live.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <Tooltip
                      title={
                        r.used_count > 0
                          ? 'Archive: recordings keep it, the app stops offering it'
                          : 'Remove'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${r.name}`}
                          disabled={busy}
                          onClick={() =>
                            void run('remove the role', async () => {
                              const outcome = await removeSessionRole(tenant.id, r.id);
                              setNotice(
                                outcome === 'archived'
                                  ? `${r.name} is archived: recordings keep it, the app no longer offers it.`
                                  : `${r.name} removed.`,
                              );
                              await reload();
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Stack>
            ))}
          </Stack>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <TextField
                size="small"
                label="New role"
                placeholder="e.g. Primary nurse"
                value={newRole}
                inputProps={{ maxLength: 80 }}
                onChange={(e) => setNewRole(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newRole.trim()) {
                    e.preventDefault();
                    void run('add the role', async () => {
                      await createSessionRole(tenant.id, newRole);
                      setNewRole('');
                      await reload();
                    });
                  }
                }}
                sx={{ flex: 1, maxWidth: 360 }}
              />
              <Button
                variant="outlined"
                disabled={busy || !newRole.trim()}
                onClick={() =>
                  void run('add the role', async () => {
                    await createSessionRole(tenant.id, newRole);
                    setNewRole('');
                    await reload();
                  })
                }
              >
                Add role
              </Button>
            </Stack>
          )}

          {archived.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Archived — kept on past recordings, not offered in the app
              </Typography>
              {archived.map((r) => (
                <Stack key={r.id} direction="row" alignItems="center" spacing={1}>
                  <Typography color="text.secondary" sx={{ flex: 1 }}>
                    {r.name}
                  </Typography>
                  {canWrite && (
                    <Tooltip title="Restore">
                      <IconButton
                        size="small"
                        aria-label={`Restore ${r.name}`}
                        disabled={busy}
                        onClick={() =>
                          void run('restore the role', async () => {
                            await updateSessionRole(tenant.id, r.id, { archived: false });
                            await reload();
                          })
                        }
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Starting a recording
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Teams often start the recording, then scrub in and go through the checks before the
            procedure really begins. With <b>Ready, then Go</b>, everything is recorded from
            Ready, but only what follows Go counts: the minutes before it are kept as standby and
            left out of the scores, the duration and the reports. Go can also be pressed on the{' '}
            <b>Live</b> page, or set afterwards on the session.
          </Typography>
          <RadioGroup
            value={startMode}
            onChange={(e) =>
              void patchTenant({ start_mode: e.target.value as StartMode }, 'change how recordings start')
            }
          >
            {START_MODES.map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                disabled={!canWrite || busy}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.help}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.25 } }}
              />
            ))}
          </RadioGroup>
          {startMode === 'voice' && (
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
              <TextField
                size="small"
                label="Spoken phrase"
                placeholder="e.g. time out complete"
                value={phrase}
                disabled={!canWrite}
                inputProps={{ maxLength: 80 }}
                onChange={(e) => setPhrase(e.target.value)}
                helperText="At least two everyday English words, said only when the procedure starts (not part of the checklist read before it). The phone listens offline and confirms with a tone when it hears them."
                sx={{ flex: 1, maxWidth: 420 }}
                error={startMode === 'voice' && !tenant.go_phrase && !phrase.trim()}
              />
              {canWrite && (
                <Button
                  variant="contained"
                  disabled={busy || !phraseDirty}
                  onClick={() =>
                    void patchTenant({ go_phrase: phrase.trim() }, 'save the phrase')
                  }
                >
                  Save phrase
                </Button>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
